import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Availability : famille "règles de disponibilité".
 *
 * Règles récurrentes et exceptions décrivant QUAND un intervenant, une ressource
 * ou un site peut recevoir des réservations, et quand tout est fermé. Construite
 * sur le patron "famille + type" (cf. asset, le PATRON) : le socle ne connaît pas
 * d'horaire concret ("heures d'ouverture du salon", "planning de la salle 2"), il
 * connaît la famille générique `availability` ; le vocabulaire métier est porté
 * par le champ `type` (chaîne libre déclarée par l'app : "recurring", "exception",
 * "opening_hours"...), jamais par le nom de collection.
 *
 * La CIBLE de la règle (targetRef) est un intervenant, une ressource ou un lieu :
 * on la modélise par de simples ids optionnels (workerId, resourceId, placeId),
 * aucun n'étant seul obligatoire (une règle peut viser l'un OU l'autre). Les
 * réservations, shifts et absences pointent VERS l'availability de LEUR côté
 * (relations un-à-plusieurs) : on ne les stocke donc pas ici.
 *
 * Convention de patron (cf. asset) :
 *   - `availabilityCoreFields`        : les champs MÉTIER seuls (sans champs standard).
 *   - `AvailabilitySchema`            : famille complète = champs standard + métier.
 *   - `AvailabilityCreateInputSchema` : ce que l'appelant fournit à la création
 *     (champs métier ; les champs standard sont remplis par le SDK). Sert à
 *     VALIDER l'entrée AVANT toute écriture Firestore.
 */

/** Champs métier de la famille availability (hors champs standard du socle). */
export const availabilityCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "recurring", "exception"). */
  type: z.string().min(1),
  /** Cible : intervenant concerné par la règle (réf. famille worker), optionnel. */
  workerId: z.string().min(1).optional(),
  /** Cible : ressource concernée par la règle (réf. famille resource), optionnel. */
  resourceId: z.string().min(1).optional(),
  /** Cible : lieu concerné par la règle (réf. famille place), optionnel. */
  placeId: z.string().min(1).optional(),
  /** Règles hebdomadaires récurrentes (structure libre par règle : jour, plage horaire...). */
  weeklyRules: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Exceptions ponctuelles (fermetures ou ouvertures exceptionnelles), structure libre par entrée. */
  exceptions: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Début de validité de la règle, optionnel. */
  validFrom: z.date().optional(),
  /** Fin de validité de la règle, optionnel. */
  validTo: z.date().optional(),
  /** Fuseau horaire de référence des plages (ex. "Europe/Luxembourg"), optionnel. */
  timezone: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille availability (champs standard + champs métier). */
export const AvailabilitySchema = z.object({
  ...baseEntityFields,
  ...availabilityCoreFields,
});

/** Type d'une availability. */
export type Availability = z.infer<typeof AvailabilitySchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const AvailabilityCreateInputSchema = z.object({
  ...availabilityCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une availability. */
export type AvailabilityCreateInput = z.infer<typeof AvailabilityCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const AvailabilityUpdateInputSchema = z
  .object({ ...availabilityCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une availability. */
export type AvailabilityUpdateInput = z.infer<typeof AvailabilityUpdateInputSchema>;
